/**
 * /payroll/year-end — Year-end forms hub
 *
 * Landing page for W-2, 1099-NEC, 1099-MISC, 941, 940, W-3.
 * Year selector + status cards per form, with deep links to per-form pages.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HistoryIcon from "@mui/icons-material/History";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end")({
  component: YearEndHubPage,
});

interface YearEndOverview {
  tax_year: number;
  employees_active: number;
  contractors_active: number;
  annual_wages: string;
  fit_withheld: string;
  ss_tax: string;
  medicare_tax: string;
  futa_tax: string;
  submissions_by_form: Record<
    string,
    { latest_status: string; latest_submitted_at: string; submission_id: string; environment: string; record_count: number }
  >;
}

function fmtMoney(s: string | number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(s));
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

function StatusChip({ status }: { status?: string }) {
  if (!status) return <Chip size="small" label="Not filed" color="default" />;
  const color =
    status === "accepted" || status === "transmitted"
      ? "success"
      : status === "dry_run"
      ? "warning"
      : status === "rejected"
      ? "error"
      : "info";
  return <Chip size="small" label={status} color={color} />;
}

function FormCard({
  title,
  description,
  to,
  icon,
  status,
  recordCount,
  environment,
}: {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
  status?: string;
  recordCount?: number;
  environment?: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardActionArea component={Link} to={to} sx={{ height: "100%", alignItems: "stretch" }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <Box sx={{ color: "primary.main" }}>{icon}</Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {description}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <StatusChip status={status} />
            {recordCount !== undefined && (
              <Chip size="small" label={`${recordCount} records`} variant="outlined" />
            )}
            {environment && environment !== "production" && (
              <Chip size="small" label={`env: ${environment}`} variant="outlined" color="warning" />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function YearEndHubPage() {
  const [year, setYear] = useState(CURRENT_YEAR);

  const { data, isLoading } = useQuery({
    queryKey: ["payroll", "year-end", "overview", year],
    queryFn: async () => {
      return (await api(`/api/payroll/year-end/${year}/overview`)) as YearEndOverview;
    },
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Year-End Forms
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate, file, and transmit annual payroll forms — direct to the IRS via IRIS, no Aatrix.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/README.md" label="Year-end filing guide" />
          </Box>
        </Box>
        <TextField
          select
          label="Tax Year"
          size="small"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {YEARS.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : data ? (
        <>
          {/* Summary stats */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Active Employees
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {data.employees_active}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Active Contractors
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {data.contractors_active}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Annual Wages
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {fmtMoney(data.annual_wages)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  FIT Withheld
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {fmtMoney(data.fit_withheld)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  FUTA Tax
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {fmtMoney(data.futa_tax)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Form cards */}
          <Typography variant="h6" fontWeight={600} mb={1.5}>
            Annual Forms
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <FormCard
              title="W-2 Wage and Tax Statement"
              description="One per employee — wages, FIT, FICA, state tax. Transmits to SSA via IRIS."
              to={`/payroll/year-end/w2/${year}`}
              icon={<DescriptionIcon />}
              status={data.submissions_by_form["W-2"]?.latest_status}
              recordCount={data.submissions_by_form["W-2"]?.record_count}
              environment={data.submissions_by_form["W-2"]?.environment}
            />
            <FormCard
              title="W-3 Transmittal"
              description="Summary of W-2 totals. Auto-generated for paper filing only — IRIS handles SSA submission."
              to={`/payroll/year-end/w3/${year}`}
              icon={<AssignmentIcon />}
            />
            <FormCard
              title="1099-NEC Nonemployee Comp"
              description="$600+ paid to contractors for services. Transmits to IRS via IRIS."
              to={`/payroll/year-end/1099-nec/${year}`}
              icon={<ReceiptLongIcon />}
              status={data.submissions_by_form["1099-NEC"]?.latest_status}
              recordCount={data.submissions_by_form["1099-NEC"]?.record_count}
              environment={data.submissions_by_form["1099-NEC"]?.environment}
            />
            <FormCard
              title="1099-MISC Miscellaneous"
              description="Rents, royalties, attorney, medical, prizes, other non-NEC payments."
              to={`/payroll/year-end/1099-misc/${year}`}
              icon={<ReceiptLongIcon />}
              status={data.submissions_by_form["1099-MISC"]?.latest_status}
              recordCount={data.submissions_by_form["1099-MISC"]?.record_count}
              environment={data.submissions_by_form["1099-MISC"]?.environment}
            />
            <FormCard
              title="Form 940 — FUTA Annual"
              description="Federal unemployment tax return. Includes Schedule A for multi-state filers."
              to={`/payroll/year-end/940/${year}`}
              icon={<AccountBalanceIcon />}
            />
          </Box>

          <Typography variant="h6" fontWeight={600} mb={1.5}>
            Quarterly Forms
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {[1, 2, 3, 4].map((q) => (
              <FormCard
                key={q}
                title={`Form 941 — Q${q} ${year}`}
                description={`Quarterly federal payroll tax return for ${year} Q${q}.`}
                to={`/payroll/year-end/941/${year}/${q}`}
                icon={<AccountBalanceIcon />}
              />
            ))}
          </Box>

          <Typography variant="h6" fontWeight={600} mb={1.5}>
            Operations
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to="/payroll/tax-deposits"
              variant="outlined"
              startIcon={<HistoryIcon />}
            >
              Tax Deposit Ledger
            </Button>
            <Button
              component={Link}
              to="/payroll/iris-submissions"
              variant="outlined"
              startIcon={<HistoryIcon />}
            >
              IRS IRIS Submission Log
            </Button>
          </Stack>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>IRIS environment:</strong> Set to <code>test</code> by default — submissions are
            validated but not transmitted to the IRS. To enable live filing, an admin must set{" "}
            <code>iris_environment=production</code> via{" "}
            <Link to="/settings/integrations">Settings → Integrations</Link>.
          </Alert>
        </>
      ) : null}
    </Box>
  );
}
